/**
 * duration.ts
 * Shared duration formatting utilities used by steppers and any other time-display UI.
 */

/**
 * Format a number of whole seconds into a human-readable string.
 * Shows every non-zero unit, e.g. "1yr 2mo 3wk 4d 5hr 6m 7s" or "500ms".
 */
export const formatDuration = (seconds: number): string => {
    if (seconds < 0) return '';
    const totalMs = Math.round(seconds * 1000);
    if (totalMs < 1000) return `${totalMs}ms`;
    let rem = Math.floor(totalMs / 1000);
    const yr = Math.floor(rem / 31536000); rem %= 31536000;
    const mo = Math.floor(rem / 2592000); rem %= 2592000;
    const wk = Math.floor(rem / 604800); rem %= 604800;
    const d = Math.floor(rem / 86400); rem %= 86400;
    const h = Math.floor(rem / 3600); rem %= 3600;
    const m = Math.floor(rem / 60);
    const s = rem % 60;
    const parts: string[] = [];
    if (yr) parts.push(`${yr}yr`);
    if (mo) parts.push(`${mo}mo`);
    if (wk) parts.push(`${wk}wk`);
    if (d) parts.push(`${d}d`);
    if (h) parts.push(`${h}hr`);
    if (m) parts.push(`${m}m`);
    if (s) parts.push(`${s}s`);
    return parts.join(' ') || '0s';
};

/**
 * Compute the human-readable duration between two ISO date strings.
 * Handles sub-second differences, negative diffs (clamped to 0), and invalid dates.
 * Returns undefined when either value is missing or unparseable.
 */
export const calcDuration = (
    from?: string | null,
    to?: string | null,
): string | undefined => {
    if (!from || !to || from === '-' || to === '-') return undefined;
    let diffMs = new Date(to).getTime() - new Date(from).getTime();
    if (isNaN(diffMs)) return undefined;
    if (diffMs < 0) diffMs = 0; // slight out-of-order timestamps
    if (diffMs < 1000) return `${diffMs}ms`;
    return formatDuration(diffMs / 1000);
};
